/**
 * Price Per Unit Updater
 * Updates price per unit displays when Skio selling plan changes
 * Simplified version using data attributes from Liquid
 */

class PricePerUnitUpdater {
  constructor() {
    this.skioPlanPickers = new Map()
    this.init()
  }

  init() {
    // Listen for Skio plan picker updates
    document.addEventListener('skio::update-selling-plan', (event) => {
      this.handleSkioPlanChange(event.detail)
    })

    // Listen for variant changes
    document.addEventListener('variant:change', (event) => {
      this.handleVariantChange(event.detail)
    })

    // Initialize Skio plan pickers
    this.initializeSkioPlanPickers()

    // Check initial state
    this.checkInitialState()
    setTimeout(() => this.checkInitialState(), 500)
    setTimeout(() => this.checkInitialState(), 1500)
  }

  initializeSkioPlanPickers() {
    const planPickers = document.querySelectorAll('skio-plan-picker')
    planPickers.forEach((picker) => {
      if (picker.key) {
        this.skioPlanPickers.set(picker.key, picker)
      }
    })

    // Watch for new pickers
    const observer = new MutationObserver(() => {
      const newPickers = document.querySelectorAll('skio-plan-picker')
      newPickers.forEach((picker) => {
        if (picker.key && !this.skioPlanPickers.has(picker.key)) {
          this.skioPlanPickers.set(picker.key, picker)
        }
      })
    })
    observer.observe(document.body, { childList: true, subtree: true })
  }

  checkInitialState() {
    const planPickers = document.querySelectorAll('skio-plan-picker')
    planPickers.forEach((picker) => {
      if (picker.key) {
        this.skioPlanPickers.set(picker.key, picker)
      }
    })

    this.skioPlanPickers.forEach((planPicker) => {
      if (!planPicker.selectedVariant || !planPicker.product) return

      const selectedPlan = planPicker.selectedSellingPlan || this.getSellingPlanFromGroup(planPicker)

      this.updateAllVariants(planPicker, selectedPlan)
    })
  }

  getSellingPlanFromGroup(planPicker) {
    const group = planPicker.selectedSellingPlanGroup
    if (!group) return null
    return group.selected_selling_plan || group.selling_plans?.[0] || null
  }

  handleSkioPlanChange(detail) {
    const { sellingPlan, key } = detail
    const planPicker = this.skioPlanPickers.get(key)
    if (!planPicker || !planPicker.product) return
    this.updateAllVariants(planPicker, sellingPlan)
  }

  handleVariantChange(detail) {
    this.skioPlanPickers.forEach((planPicker) => {
      if (!planPicker.product?.variants) return
      const variantExists = planPicker.product.variants.some(v => v.id == detail.variant?.id)
      if (variantExists) {
        this.updateAllVariants(planPicker, planPicker.selectedSellingPlan)
      }
    })
  }

  updateAllVariants(planPicker, sellingPlan) {
    const elements = document.querySelectorAll('.price-per-unit[data-variant-id]')
    if (!elements.length) return

    const variantMap = new Map()
    planPicker.product.variants.forEach(v => {
      variantMap.set(String(v.id), v)
    })

    const originalVariant = planPicker.selectedVariant

    elements.forEach((element) => {
      const variantId = element.getAttribute('data-variant-id')
      const basePrice = parseInt(element.getAttribute('data-base-price'), 10)
      const unitAmount = parseFloat(element.getAttribute('data-unit-amount'))
      const measurementUnit = element.getAttribute('data-measurement-unit')
      
      if (!variantId || !basePrice || !unitAmount || !measurementUnit) return

      const variant = variantMap.get(variantId)
      if (!variant) return

      // Calculate discounted price
      let price = basePrice
      
      if (sellingPlan) {
        try {
          planPicker.selectedVariant = variant
          if (typeof planPicker.price === 'function') {
            price = planPicker.price(sellingPlan, false)
          }
        } catch (e) {
          console.warn(`Failed to calculate price for variant ${variantId}:`, e)
        }
      }

      // Calculate and update price per unit
      const pricePerUnit = price / unitAmount
      const formattedPrice = this.formatMoney(pricePerUnit, planPicker)
      element.textContent = `${formattedPrice}/${measurementUnit}`
    })

    if (originalVariant) {
      planPicker.selectedVariant = originalVariant
    }
  }

  formatMoney(cents, planPicker) {
    if (planPicker && typeof planPicker.money === 'function') {
      return planPicker.money(cents)
    }

    const moneyFormat = window.Shopify?.shop?.money_format || '{{amount}}'
    const amount = (cents / 100).toFixed(2)
    
    return moneyFormat.replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString().replace('.', ','))
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PricePerUnitUpdater()
  })
} else {
  new PricePerUnitUpdater()
}
